import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  tokens,
  shorthands,
  Divider,
  Badge,
  Spinner
} from '@fluentui/react-components';
import { Dismiss24Regular, Attach24Regular } from '@fluentui/react-icons';
import { EmailPreviewResponse } from '../../types/email.types';

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  preview: EmailPreviewResponse | null;
  loading: boolean;
  filename?: string;
}

const useStyles = makeStyles({
  dialog: {
    maxWidth: '600px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeButton: {
    minWidth: 'auto',
  },
  metadataSection: {
    ...shorthands.margin(tokens.spacingVerticalM, "0"),
  },
  metadataRow: {
    display: 'flex',
    ...shorthands.padding(tokens.spacingVerticalS, "0"),
    fontSize: tokens.fontSizeBase300,
  },
  metadataLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '80px',
    color: tokens.colorNeutralForeground3,
  },
  metadataValue: {
    flex: 1,
    color: tokens.colorNeutralForeground1,
  },
  bodySection: {
    ...shorthands.margin(tokens.spacingVerticalL, "0"),
    ...shorthands.padding(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    minHeight: '150px',
  },
  attachmentSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.margin(tokens.spacingVerticalM, "0"),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  }
});

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({ 
  isOpen, 
  onClose, 
  preview, 
  loading,
  filename 
}) => {
  const styles = useStyles();

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle>
            <div className={styles.header}>
              <span>Email Preview {filename && `- ${filename}`}</span>
              <Button
                className={styles.closeButton}
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            </div>
          </DialogTitle>
          
          <DialogContent>
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spinner label="Loading preview..." />
              </div>
            ) : preview ? (
              <>
                <div className={styles.metadataSection}>
                  <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>Subject:</span>
                    <span className={styles.metadataValue}>{preview.subject || 'No Subject'}</span>
                  </div>
                  <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>From:</span>
                    <span className={styles.metadataValue}>{preview.from}</span>
                  </div>
                  <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>To:</span>
                    <span className={styles.metadataValue}>{preview.to}</span>
                  </div>
                  <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>Date:</span>
                    <span className={styles.metadataValue}>{preview.date}</span>
                  </div>
                </div>

                <Divider />

                {preview.hasAttachments && (
                  <div className={styles.attachmentSection}>
                    <Attach24Regular />
                    <span>{preview.attachmentCount} attachment{preview.attachmentCount !== 1 ? 's' : ''}</span>
                  </div>
                )}

                <div className={styles.bodySection}>
                  <p>{preview.bodyPreview || 'No content preview available'}</p>
                </div>
              </>
            ) : (
              <p>Failed to load preview</p>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default EmailPreviewModal;
